import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import { useToast } from '@/shared/contexts/ToastContext'

import {
  createTracingReportRule,
  deleteTracingReportRule,
  generateTracingReport,
  getTracingReportItems,
  getTracingReportRules,
} from '../apis/tracingReport'
import { type TracingReportItem } from '../types/tracingReport'

export const useTracingReportRulesQuery = () => {
  return useQuery({
    queryKey: queryKeys.tracingReport.rules,
    queryFn: getTracingReportRules,
    staleTime: 1000 * 60 * 5,
  })
}

export const useCreateTracingReportRuleMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: createTracingReportRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tracingReport.rules })
      showToast('ルールを作成しました', 'success')
    },
    onError: () => {
      showToast('ルールの作成に失敗しました', 'error')
    },
  })
}

export const useDeleteTracingReportRuleMutation = () => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: deleteTracingReportRule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tracingReport.rules })
      showToast('ルールを削除しました', 'success')
    },
    onError: () => {
      showToast('ルールの削除に失敗しました', 'error')
    },
  })
}

// セッションの item 一覧。SOAP 後処理の生成ラグを polling で拾う
// (未生成 item がある間だけ 5 秒間隔)。
export const useTracingReportItemsQuery = (
  yakurekiId: string | undefined,
  options: { enabled?: boolean } = {}
) => {
  return useQuery({
    queryKey: queryKeys.tracingReport.items(yakurekiId ?? ''),
    queryFn: () => getTracingReportItems(yakurekiId as string),
    enabled: Boolean(yakurekiId) && (options.enabled ?? true),
    refetchInterval: (query) => {
      const items = query.state.data as TracingReportItem[] | undefined
      if (items && items.some((item) => item.report_json === null)) return 5000
      return false
    },
  })
}

export const useGenerateTracingReportMutation = (yakurekiId: string) => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()

  return useMutation({
    mutationFn: () => generateTracingReport(yakurekiId),
    onSuccess: (items) => {
      queryClient.setQueryData(queryKeys.tracingReport.items(yakurekiId), items)
    },
    onError: () => {
      showToast('トレーシングレポートの作成に失敗しました', 'error')
    },
  })
}
